// Mythicals — ultra-rare, single-slot, consumable items that drop from
// chests (~8% of correct-team loot). One-shot cinematics. Distinct from
// Legendary boss drops (those are permanent stat buffs).
//
// deathnote    — instakill one enemy at any range within MAX_RANGE.
//                Gated by MYTHIC_DEATHNOTE_LOCKOUT_SEC from match start.
// going_viral  — Mario-star buff for MYTHIC_VIRAL_DUR_MS. Speed ×2,
//                damage ×1.6, music tempo ×1.4 (local-only), rainbow
//                outline visible to everyone.
// shockwave    — AoE nuke. All enemies within MYTHIC_SHOCKWAVE_RANGE
//                instantly take MYTHIC_SHOCKWAVE_DMG. Self-targeted
//                (no picker). Bots are one-shot, full-HP humans are
//                half-shot — turns a desperate position into a clutch
//                triple frag.

import type { MythicalId } from '../net/protocol';

export type MythicalDef = {
  id: MythicalId;
  name: string;
  icon: string;      // single emoji for HUD pill fallback
  color: string;     // accent color for pill / pickup orb
  blurb: string;     // tooltip/HUD subtitle
  kind: 'targeted' | 'self'; // targeted = opens picker; self = instant use
};

export const MYTHICALS: Record<MythicalId, MythicalDef> = {
  deathnote: {
    id: 'deathnote',
    name: 'DEATH NOTE',
    icon: '📓',
    color: '#0f172a',
    blurb: 'Write a name. End them.',
    kind: 'targeted',
  },
  going_viral: {
    id: 'going_viral',
    name: 'GOING VIRAL',
    icon: '🌟',
    color: '#fde047',
    blurb: '6s of pure hype — ×2 speed, ×1.6 damage',
    kind: 'self',
  },
  shockwave: {
    id: 'shockwave',
    name: 'SHOCKWAVE',
    icon: '💥',
    color: '#f0abfc',
    blurb: 'Nuke every enemy within 8u',
    kind: 'self',
  },
  mind_meld: {
    id: 'mind_meld',
    name: 'MIND MELD',
    icon: '🧠',
    color: '#a78bfa',
    blurb: '6s — every enemy lit up like a beacon',
    kind: 'self',
  },
  rewind: {
    id: 'rewind',
    name: 'REWIND',
    icon: '⏪',
    color: '#22d3ee',
    blurb: 'Snap back 4s — undo HP, position, ult spend',
    kind: 'self',
  },
};

export const MYTHICAL_IDS: MythicalId[] = ['deathnote', 'going_viral', 'shockwave', 'mind_meld', 'rewind'];

// Drop roll: when a chest spawns a same-team loot item (xp/hp), this
// returns a MythicalId to replace it with, or null to keep the stock
// drop. 8% rate = ~1 mythic per 12 pickups = ~1 per match on average.
// Even 5-way split — each mythic has equal odds.
export function rollMythicForLoot(): MythicalId | null {
  if (Math.random() > 0.08) return null;
  const r = Math.random();
  if (r < 0.20) return 'deathnote';
  if (r < 0.40) return 'going_viral';
  if (r < 0.60) return 'shockwave';
  if (r < 0.80) return 'mind_meld';
  return 'rewind';
}

export function mythicalName(id: MythicalId): string {
  return MYTHICALS[id].name;
}

export function mythicalBlurb(id: MythicalId): string {
  return MYTHICALS[id].blurb;
}
